import type { HappyHour, Outlet, PrintDestination } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  EmptyState,
  NoPermissionState,
  PageHeader,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Th,
  TicketText,
  Tr,
  toast,
} from '@horevia/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Pencil, Plus, Printer, Sparkles, Store } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { HappyHourDrawer } from '@/features/pos/HappyHourDrawer';
import { DestinationDrawer, OutletDrawer } from '@/features/pos/SettingsDrawers';
import { happyHourDiscountLabel } from '@/features/pos/happy-hour';
import { useCurrentOutlet } from '@/features/pos/outlet';
import {
  categoriesQueryOptions,
  destinationsQueryOptions,
  happyHoursQueryOptions,
  outletsQueryOptions,
  testDestination,
} from '@/features/pos/queries';
import { errorMessage } from '@/lib/errors';
import { useCurrentProperty } from '@/lib/property';

export const Route = createFileRoute('/_app/pos/settings')({ component: PosSettingsPage });

/** Points de vente de l'établissement (ADR-015) et destinations d'impression (ADR-017). */
function PosSettingsPage() {
  const { t } = useTranslation(['pos', 'common', 'errors']);
  const session = useSession();
  const { property } = useCurrentProperty();
  const { outlet, setOutletId } = useCurrentOutlet();
  const canRead = session.can('outlet.read');
  const canManageOutlets = session.can('outlet.manage');
  const canReadPrinting = session.can('printing.read');
  const canManagePrinting = session.can('printing.manage');
  // Happy hours (module Bar, ADR-019) : règles de prix de la carte → permissions catalogue.
  const hasBar = session.hasModule('bar');
  const canReadHappyHours = hasBar && session.can('catalog.read');
  const canManageHappyHours = hasBar && session.can('catalog.manage');
  const [outletDrawer, setOutletDrawer] = useState<{ open: boolean; outlet: Outlet | null }>({
    open: false,
    outlet: null,
  });
  const [destinationDrawer, setDestinationDrawer] = useState<{
    open: boolean;
    destination: PrintDestination | null;
  }>({ open: false, destination: null });
  const [testOutput, setTestOutput] = useState<string | null>(null);
  const [happyHourDrawer, setHappyHourDrawer] = useState<{
    open: boolean;
    happyHour: HappyHour | null;
  }>({ open: false, happyHour: null });

  const outlets = useQuery(outletsQueryOptions(property?.id, canRead && Boolean(property), true));
  const destinations = useQuery(
    destinationsQueryOptions(outlet?.id ?? '', canReadPrinting && Boolean(outlet), true),
  );
  const happyHours = useQuery(
    happyHoursQueryOptions(outlet?.id ?? '', canReadHappyHours && Boolean(outlet), true),
  );
  const categories = useQuery(
    categoriesQueryOptions(
      { propertyId: property?.id, outletId: outlet?.id },
      canReadHappyHours && Boolean(outlet),
    ),
  );
  const categoryName = (id: string) => categories.data?.find((c) => c.id === id)?.name ?? '?';
  const test = useMutation({
    mutationFn: (destination: PrintDestination) => testDestination(destination.id),
    onSuccess: (result) => {
      toast.success(result.printed ? t('pos:settings.tested') : t('pos:settings.testedBrowser'));
      setTestOutput(result.content);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('pos:settings.title')}
        subtitle={t('pos:settings.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('pos:settings.title') },
        ]}
        actions={
          canManageOutlets && property ? (
            <Button variant="primary" onClick={() => setOutletDrawer({ open: true, outlet: null })}>
              <Plus className="ico" aria-hidden="true" />
              {t('pos:settings.newOutlet')}
            </Button>
          ) : undefined
        }
      />

      <div className="stack">
        <Card>
          <CardHeader
            title={t('pos:settings.outlets')}
            icon={<Store className="ico" aria-hidden="true" />}
          />
          <CardBody density="flush">
            {outlets.isPending ? (
              <Skeleton height={120} />
            ) : (outlets.data ?? []).length === 0 ? (
              <EmptyState
                title={t('pos:settings.emptyOutlets')}
                description={t('pos:settings.emptyOutletsHint')}
              />
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('pos:settings.fields.code')}</Th>
                      <Th>{t('pos:settings.fields.name')}</Th>
                      <Th>{t('pos:settings.fields.kind')}</Th>
                      <Th />
                    </tr>
                  </thead>
                  <tbody>
                    {(outlets.data ?? []).map((item) => (
                      <Tr key={item.id} selected={item.id === outlet?.id}>
                        <Td strong>{item.code}</Td>
                        <Td>
                          {item.name}
                          {!item.isActive ? (
                            <>
                              {' '}
                              <Badge tone="neutral">{t('pos:settings.inactive')}</Badge>
                            </>
                          ) : null}
                        </Td>
                        <Td>{t(`pos:settings.outletKind.${item.kind}`)}</Td>
                        <Td align="right">
                          <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                            {item.isActive && item.id !== outlet?.id ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setOutletId(item.id)}
                              >
                                {t('pos:settings.destinations')}
                              </Button>
                            ) : null}
                            {canManageOutlets ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setOutletDrawer({ open: true, outlet: item })}
                              >
                                <Pencil className="ico ico-16" aria-hidden="true" />
                                {t('common:actions.edit')}
                              </Button>
                            ) : null}
                          </span>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </CardBody>
        </Card>

        {canReadPrinting && outlet ? (
          <Card>
            <CardHeader
              title={`${t('pos:settings.destinations')} — ${outlet.name}`}
              icon={<Printer className="ico" aria-hidden="true" />}
              actions={
                canManagePrinting ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setDestinationDrawer({ open: true, destination: null })}
                  >
                    <Plus className="ico ico-16" aria-hidden="true" />
                    {t('pos:settings.newDestination')}
                  </Button>
                ) : undefined
              }
            />
            <CardBody density="flush">
              {destinations.isPending ? (
                <Skeleton height={120} />
              ) : (destinations.data ?? []).length === 0 ? (
                <EmptyState
                  title={t('pos:settings.emptyDestinations')}
                  description={t('pos:settings.emptyDestinationsHint')}
                />
              ) : (
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('pos:settings.fields.code')}</Th>
                        <Th>{t('pos:settings.fields.name')}</Th>
                        <Th>{t('pos:settings.fields.kind')}</Th>
                        <Th>{t('pos:settings.fields.driver')}</Th>
                        <Th />
                      </tr>
                    </thead>
                    <tbody>
                      {(destinations.data ?? []).map((destination) => (
                        <Tr key={destination.id}>
                          <Td strong>{destination.code}</Td>
                          <Td>
                            {destination.name}{' '}
                            {destination.isDefault ? (
                              <Badge tone="green">{t('pos:settings.default')}</Badge>
                            ) : null}{' '}
                            {!destination.isActive ? (
                              <Badge tone="neutral">{t('pos:settings.inactive')}</Badge>
                            ) : null}
                          </Td>
                          <Td>{t(`pos:settings.destinationKind.${destination.kind}`)}</Td>
                          <Td>
                            {t(`pos:settings.driver.${destination.driver}`)}
                            {destination.driver === 'ESCPOS_NETWORK' ? (
                              <span className="hint" style={{ display: 'block' }}>
                                {destination.settings.host}:{destination.settings.port} ·{' '}
                                {destination.settings.width} mm
                              </span>
                            ) : null}
                          </Td>
                          <Td align="right">
                            <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                              {canManagePrinting ? (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={test.isPending}
                                    onClick={() => test.mutate(destination)}
                                  >
                                    <Printer className="ico ico-16" aria-hidden="true" />
                                    {t('pos:settings.test')}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      setDestinationDrawer({ open: true, destination })
                                    }
                                  >
                                    <Pencil className="ico ico-16" aria-hidden="true" />
                                    {t('common:actions.edit')}
                                  </Button>
                                </>
                              ) : null}
                            </span>
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
              {testOutput ? <TicketText>{testOutput}</TicketText> : null}
            </CardBody>
          </Card>
        ) : null}
        {canReadHappyHours && outlet ? (
          <Card>
            <CardHeader
              title={`${t('pos:settings.happyHours')} — ${outlet.name}`}
              icon={<Sparkles className="ico" aria-hidden="true" />}
              actions={
                canManageHappyHours ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setHappyHourDrawer({ open: true, happyHour: null })}
                  >
                    <Plus className="ico ico-16" aria-hidden="true" />
                    {t('pos:settings.newHappyHour')}
                  </Button>
                ) : undefined
              }
            />
            <CardBody density="flush">
              {happyHours.isPending ? (
                <Skeleton height={120} />
              ) : (happyHours.data ?? []).length === 0 ? (
                <EmptyState
                  title={t('pos:settings.emptyHappyHours')}
                  description={t('pos:settings.emptyHappyHoursHint')}
                />
              ) : (
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('pos:settings.happyHourFields.name')}</Th>
                        <Th>{t('pos:settings.happyHourFields.schedule')}</Th>
                        <Th>{t('pos:settings.happyHourFields.discountType')}</Th>
                        <Th>{t('pos:settings.happyHourFields.scope')}</Th>
                        <Th />
                      </tr>
                    </thead>
                    <tbody>
                      {(happyHours.data ?? []).map((rule) => (
                        <Tr key={rule.id}>
                          <Td strong>
                            {rule.name}
                            {!rule.isActive ? (
                              <>
                                {' '}
                                <Badge tone="neutral">{t('pos:settings.inactive')}</Badge>
                              </>
                            ) : null}
                          </Td>
                          <Td>
                            {rule.daysOfWeek.map((day) => t(`pos:settings.days.${day}`)).join(' ')}
                            {' · '}
                            {rule.startTime} – {rule.endTime}
                          </Td>
                          <Td>{happyHourDiscountLabel(rule, session.user.tenant.currencyCode)}</Td>
                          <Td>
                            {rule.categoryIds.length === 0
                              ? t('pos:settings.happyHourFields.allCategories')
                              : rule.categoryIds.map(categoryName).join(', ')}
                          </Td>
                          <Td align="right">
                            {canManageHappyHours ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setHappyHourDrawer({ open: true, happyHour: rule })}
                              >
                                <Pencil className="ico ico-16" aria-hidden="true" />
                                {t('common:actions.edit')}
                              </Button>
                            ) : null}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </CardBody>
          </Card>
        ) : null}
      </div>

      {property ? (
        <OutletDrawer
          open={outletDrawer.open}
          onOpenChange={(open) => setOutletDrawer((current) => ({ ...current, open }))}
          propertyId={property.id}
          outlet={outletDrawer.outlet}
        />
      ) : null}
      {outlet ? (
        <DestinationDrawer
          open={destinationDrawer.open}
          onOpenChange={(open) => setDestinationDrawer((current) => ({ ...current, open }))}
          outletId={outlet.id}
          destination={destinationDrawer.destination}
        />
      ) : null}
      {outlet ? (
        <HappyHourDrawer
          open={happyHourDrawer.open}
          onOpenChange={(open) => setHappyHourDrawer((current) => ({ ...current, open }))}
          outletId={outlet.id}
          categories={categories.data ?? []}
          currencyCode={session.user.tenant.currencyCode}
          happyHour={happyHourDrawer.happyHour}
        />
      ) : null}
    </Container>
  );
}
